import React, { useState } from 'react';
import styled from 'styled-components'
import HelpOutline from '@material-ui/icons/HelpOutline';
import ManageIcon from '@material-ui/icons/AccountBox';
import MyAccountIcon from '@material-ui/icons/Https';
import AvatarIcon from '@material-ui/icons/InsertChartOutlined';
import CardIcon from '@material-ui/icons/CreditCard';

const Container = styled.div`
  background: #FFF;
	border-radius: 8px;
	box-shadow: 0px 0px 8px 0px #e1e2e6;
  width: 100%;
  padding: 24px;

  @media(max-width: 900px) {
    padding: 16px;
  }
`;

const Label = styled.label`
  color: #555;
  display: flex;
  align-items: center;
  font-family: ${props => props.theme.font.primary};
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 24px;

  svg {
    fill: #555;
    height: auto;
    width: 36px;
    margin-right: 16px;
  }

  @media(max-width: 900px) {
    font-size: 1.25rem;
    margin-bottom: 16px;

    svg {
      width: 28px;
    }
  }
`;

const FaqContainer = styled.div`
  display: flex;

  @media(max-width: 900px) {
    flex-direction: column;
  }
`;

const CategoryContainer = styled.div`
  flex-shrink: 0;
  padding-right: 32px;
  width: 260px;

  @media(max-width: 900px) {
    padding: 0px 0px 32px 0px;
    width: 100%;
  }
`;

const SelectCategory = styled.p`
  background: ${props => props.active ? '#efefef' : 'none'};
  border-radius: 6px;
  color: ${props => props.active ? '#04bd3b' : '#555'};
  cursor: pointer;
  display: flex;
  align-items: center;
  font-family:  ${props => props.theme.font.primary};
  font-weight: 500;
  padding: 12px 16px;
  margin-bottom: 12px;
  transition: 0.3s ease all;

  &:hover {
    color: ${props => '#04bd3b' };

    svg {
      fill: ${props => '#04bd3b' };
    }
  }

  svg {
    margin-right: 12px;
    fill: ${props => props.active ? '#04bd3b' : '#555'};
  }
`;

const QuestionContainer = styled.div`
`;

const QuestionWrapper = styled.div`
  ol {
    margin-top: 18px;
  }
  li {
    margin: 0px 0px 0px 36px;
  }
`;

const QuestionTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 24px;
`;

const QuestionAnswer = styled.p`
  margin-bottom: 56px;
`;

const Question = (props) => (
  <QuestionWrapper>
    <QuestionTitle>
      Q: {props.question}
    </QuestionTitle>
    {props.children}
    <QuestionAnswer>
      {props.answer}
    </QuestionAnswer>
  </QuestionWrapper>
)

const Faq = (props) => {
  const [category, updateCategory] = useState('avatar-nutrition')

  const renderQuestions = (category) => {
    if(category === 'avatar-nutrition') {
      return (
        <React.Fragment>
          <Question 
            question="How does someone claim their account?"
            answer="An auto-generated email will be sent to the user once the portal manager has added the new user’s email address to their list. This email will direct the user to visit app.avatarnutrition.com and complete the signup process. The user will not be prompted to enter payment information since their account is already paid for through the portal."
          />
          <Question 
            question="How long does it take for a newly added account to become accessible to the user?"
            answer="Newly added accounts are immediately accessible. Once the user has gone to app.avatarnutrition.com and completed the signup flow, their account will be ready for use."
          />
          <Question 
            question="What happens if I remove a user from the list on my portal account that has an active Avatar Nutrition Account?"
            answer="When a user is removed by the portal manager, the next time the user logs in they will receive a notification that their account is no longer being paid for through the portal system and that they will need to update payment details to continue using the service. At this time they may elect to enter their payment details and will be billed monthly going forward until they cancel their account."
          />
          <Question 
            question="How do I cancel my portal account?"
            answer="On the My Profile page, click on the Cancel Account button at the bottom."
          />
        </React.Fragment>
      )
    } else if (category === 'billing') {
      return (
        <React.Fragment>
          <Question 
            question="How is the cost calculated?"
            answer={<p>The cost per month is determined by the highest amount of seats covered each month. Each client added will occupy a seat, and each seat will cover one user at a time. If a client is removed, that seat will be open again and can be occupied by another client. <br /><br /> For example: If a client billy@abc123.com is added then immediately removed, their seat will be included in this billing cycle. If another client sam@abc123.com is added, then they will occupy this open seat and the calculated price for this month will not increase.</p>}
          />
          <Question 
            question="Who do I contact if I am having a billing issue with my portal accounts?"
            answer="Questions or concerns regarding billing should be sent by using the Contact Us form below."
          />
        </React.Fragment>
      )
    } else if (category === 'manage-accounts') {
      return (
        <React.Fragment>
          <Question 
            question="How do I add an account?"
            answer={<>
              <p>To add an account:</p>
              <ol>
                <li>Log into the portal</li>
                <li>Go to the Manage Clients page</li>
                <li>Enter the email address and full name of the user you would like to add</li>
                <li>Double check the entry to make sure that the spelling of the name and email are correct</li>
                <li>Confirm and then verify the charge in the pricing confirmation window that populates</li>
                <li>Click Add User if you wish to proceed with the charges being applied to your account</li>
              </ol>
            </>}
          >
            
          </Question>
          <Question 
            question="Are there any differences between accounts covered by a portal manager and accounts created independently?"
            answer="Accounts that are created independently by a user will be charged the standard subscription fee based on the direct to consumer price structure. When an account is created by a portal account manager, an email will be sent to the user and they will be encouraged to set-up their profile."
          />
          <Question 
            question="How do I remove or add multiple accounts?"
            answer="Please contact us at using the form below."
          />
          <Question 
            question="Who do I contact if I am having technical issues with portal accounts?"
            answer="Please use the form below if you have any questions regarding the portal."
          />
        </React.Fragment>
      )
    } else if (category === 'my-account') {
      return (
        <React.Fragment>
          <Question 
            question="I forgot my password."
          >
            <p>To reset your password:</p>
            <ol>
              <li>Go to the portal login page</li>
              <li>Click the I forgot Password link</li>
              <li>Enter the email address of the master account holder</li>
              <li>An automatic email will be generated detailing the process for resetting your password</li>
            </ol>
          </Question>
        </React.Fragment>
      )
    }
  }

  return (
    <Container className={props.className}>
      <Label>
        <HelpOutline /> Frequently Asked Questions
      </Label>
      <FaqContainer>
        <CategoryContainer>
          <SelectCategory active={(category === 'avatar-nutrition')} onClick={() => updateCategory('avatar-nutrition')}><AvatarIcon /> Avatar Nutrition</SelectCategory>
          <SelectCategory active={(category === 'billing')} onClick={() => updateCategory('billing')}><CardIcon /> Billing</SelectCategory>
          <SelectCategory active={(category === 'manage-accounts')} onClick={() => updateCategory('manage-accounts')}><ManageIcon /> Managing Accounts</SelectCategory>
          <SelectCategory active={(category === 'my-account')} onClick={() => updateCategory('my-account')}><MyAccountIcon /> My Account</SelectCategory>
        </CategoryContainer>
        <QuestionContainer>
          {renderQuestions(category)}
        </QuestionContainer>
      </FaqContainer>
    </Container>
  )
}

export default Faq